import { useEffect, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { usePage } from '@inertiajs/react'
import { AppSidebar, type NavigationPermissions } from '@/components/AppSidebar'
import { AppTopbar } from '@/components/AppTopbar'
import { FlashAlert } from '@/components/Flash/FlashAlert'
import { FlashInfo } from '@/components/Flash/FlashInfo'
import { FlashNotice } from '@/components/Flash/FlashNotice'

const FLASH_COMPONENTS = { alert: FlashAlert, info: FlashInfo, notice: FlashNotice }

interface AppLayoutProps {
  children: ReactNode
  /** Replaces the breadcrumb. Pages that own their own title pass this. */
  header?: ReactNode
  headerActions?: ReactNode
}

interface PageProps {
  user?: { name: string; email: string; admin?: boolean }
  navigation?: NavigationPermissions
  flash?: Record<string, string>
  unread_notifications_count?: number
  debug?: { controller?: string }
  environment?: string
  [key: string]: unknown
}

/**
 * The shell: sticky sidebar, glass topbar, one centred content column.
 * Permission-gated nav, the flash portal and the dev debug bar all live here,
 * expressed in tokens rather than the inline styles this file used to carry.
 */
export default function AppLayout({ children, header, headerActions }: AppLayoutProps) {
  const { url, props, component } = usePage<PageProps>()
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem('sidebar_collapsed') === 'true'
    } catch {
      return false
    }
  })
  const [dismissedFlash, setDismissedFlash] = useState(false)

  useEffect(() => {
    try {
      localStorage.setItem('sidebar_collapsed', String(collapsed))
    } catch {
      /* private window; the sidebar just opens expanded next time */
    }
  }, [collapsed])

  const flash = props.flash
  const flashMessage = !dismissedFlash && flash && (flash.notice || flash.alert || flash.info)
  const flashType: 'alert' | 'info' | 'notice' = flash?.alert
    ? 'alert'
    : flash?.info
      ? 'info'
      : 'notice'

  useEffect(() => {
    setDismissedFlash(false)
  }, [url, flash?.notice, flash?.alert, flash?.info])

  return (
    <>
      {/* First tab stop on every page: the nav and a search box sit between the
          top of the document and the content otherwise. */}
      <a
        href="#main"
        className="border-border bg-bg-elev shadow-pop focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-50 sr-only rounded-md border px-3 py-2 text-sm font-medium"
      >
        Skip to content
      </a>

      {import.meta.env.DEV && (
        <div className="bg-fg text-bg flex shrink-0 justify-center gap-2 px-3 py-0.5 text-2xs">
          <span className="text-bg/70">Debug</span>
          <span>{props.debug?.controller || '—'}</span>
          <span aria-hidden className="text-bg/40">
            ·
          </span>
          <span>pages/{component}.tsx</span>
        </div>
      )}

      <div className="bg-bg flex min-h-screen items-stretch">
        <AppSidebar
          url={url}
          collapsed={collapsed}
          onToggleCollapse={() => setCollapsed((c) => !c)}
          navigation={props.navigation || {}}
          user={props.user}
        />

        <div className="flex min-w-0 flex-1 flex-col">
          <AppTopbar
            url={url}
            unreadCount={props.unread_notifications_count || 0}
            environment={props.environment}
            header={header}
            headerActions={headerActions}
          />
          <main id="main" tabIndex={-1} className="mx-auto w-full max-w-380 px-10 py-8 outline-none">
            {children}
          </main>
        </div>
      </div>

      {flashMessage &&
        createPortal(
          (() => {
            const FlashCmp = FLASH_COMPONENTS[flashType]
            return <FlashCmp message={flashMessage} onDismiss={() => setDismissedFlash(true)} />
          })(),
          document.body,
        )}
    </>
  )
}
